import React, { useMemo } from "react";
import PropTypes from "prop-types";
import NextImage from "next/image";
import Link from "next/link";
import { unescape } from "lodash";
import LocationIcon from "components/icons/location";
import styled, { css } from "styled-components";
import generateRandomImagePlaceholderPath from "lib/generate-random-image-placeholder";
import ServiceTypePills from "components/ServiceTypePills";
import Highlights from "components/Highlights";
import Tags from "components/Tags";
import Snippets from "components/Snippets";
import BookingButton from "components/BookingButton";
import { selectItem } from "lib/data-layer-push";

const ResultWrapper = styled.div`
  border-bottom: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  padding: 1.5rem 0;
`;

const Result = styled.li`
  margin: 0 auto;
  padding: 0 1.5rem 0;

  :last-of-type > ${ResultWrapper} {
    border-bottom: 0 none;
  }

  ${({ theme }) => theme.mq.xl`
    ${({ active }) =>
      active &&
      css`
        background-color: ${theme.themeColors.lightGreen1};
      `}
  `}
`;

const Anchor = styled.a`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  position: relative;
  z-index: 1;

  ${({ theme }) => theme.mq.md`
    flex-direction: row;
  `}
`;

const ButtonWrapper = styled.div`
  font-size: 0.75rem;
  margin: 0;
  text-align: center;

  ${({ theme }) => theme.mq.md`
    margin: 0 0 0 1rem;
    min-width: 12rem;
    width: 12rem;
  `}
`;

const Row = styled.div`
  ${ButtonWrapper} {
    display: none;
  }

  ${({ theme }) => theme.mq.md`
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${ButtonWrapper} {
      display: block;
    }

    /* hides mobile button */
    + ${ButtonWrapper} {
      display: none;
    }
  `}
`;

const Column = styled.div`
  width: auto;
`;

const ColumnExpanded = styled.div`
  width: 100%;
`;

const ImageWrapper = styled.div`
  margin: 0 auto 1.5rem;
  height: calc((100vw - 3rem) / (3 / 2));
  order: 1;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    height: 6.25rem;
    margin: 0 1.5rem 0 0;
    width: 9.375rem;
  `}

  ${({ theme }) => theme.mq.xl`
    height: 9rem;
    width: 13.5rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    height: 11.75rem;
    width: 17.625rem;
  `}
`;

const Image = styled(NextImage)`
  border-radius: 0.5rem;
`;

const ServiceTypes = styled.div`
  margin: 0 0 0.5rem;
`;

const Name = styled.h3`
  margin: 0 0 0.25rem;
`;

const MapMarkerIcon = styled(LocationIcon)`
  margin: 0.125rem 0.25rem 0 0;
  vertical-align: top;
`;

const Address = styled.address`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  font-weight: 500;
  font-size: 0.875rem;
  margin: 0 0 0.75rem;
`;

const HighlightsWrapper = styled.div`
  margin: 0 0 1rem;
  overflow: hidden;
  height: 1.5rem;

  ${({ theme }) => theme.mq.md`
    max-width: 31.25rem;

    /* hides mobile button */
    + ${ButtonWrapper} {
      display: none;
    }
  `}
`;

const TagsWrapper = styled.div`
  margin: 0 0 1rem;
  height: 1.5rem;
  overflow: hidden;

  ${({ theme }) => theme.mq.md`
    max-width: 31.25rem;
    
    /* hides mobile button */
    + ${ButtonWrapper} {
      display: none;
    }
  `}
`;

const SnippetsWrapper = styled.div`
  margin: 0 0 1.5rem;
  & > ul {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  ${({ theme }) => theme.mq.md`
    margin: 0;

    /* hides mobile button */
    + ${ButtonWrapper} {
      display: none;
    }
  `}
`;

function BusinessSearchResult({
  activeBusinessId,
  bookingType,
  certifications,
  friendlyName,
  formattedAddress,
  highlights,
  id,
  imageUrl,
  isAcceptingBooking,
  name,
  neighborHood,
  onHover,
  serviceTypes,
  snippets,
  tags,
  timeSlots,
  urlLocality,
  urlRegion,
  visible,
  biz,
  serviceTypeQueryParam,
}) {
  const imageSrc = useMemo(() => {
    return imageUrl || generateRandomImagePlaceholderPath(id, serviceTypes);
  }, []);
  return (
    <Result onMouseEnter={() => onHover(id)} active={id === activeBusinessId}>
      <ResultWrapper>
        <Link passHref href={`/business/${urlRegion}/${urlLocality}/${friendlyName}${visible ? "?visible=true" : ""}`}>
          <Anchor onClick={() => selectItem(biz, serviceTypeQueryParam)}>
            <Column>
              <ImageWrapper>
                <Image alt={unescape(name)} objectFit="cover" objectPosition="center" src={imageSrc} layout="fill" />
              </ImageWrapper>
            </Column>
            <ColumnExpanded>
              <Row>
                <Column>
                  <ServiceTypes>
                    <ServiceTypePills businessName={name} parent="BusinessSearchResult" serviceTypes={serviceTypes} />
                  </ServiceTypes>
                  <Name>{unescape(name)}</Name>
                </Column>
                <ButtonWrapper>
                  <BookingButton bookingType={bookingType} id={id} timeSlots={timeSlots} isAcceptingBooking={isAcceptingBooking} />
                </ButtonWrapper>
              </Row>
              <Row>
                <Column>
                  {(neighborHood && (
                    <Address>
                      <MapMarkerIcon size={14} />
                      {neighborHood}
                    </Address>
                  )) ||
                    (formattedAddress && (
                      <Address>
                        <MapMarkerIcon size={14} />
                        {formattedAddress}
                      </Address>
                    ))}
                </Column>
              </Row>
              {(highlights?.length > 0 || certifications.length > 0) && (
                <HighlightsWrapper>
                  <Highlights data={highlights} isProfile={false} />
                </HighlightsWrapper>
              )}
              {tags?.length > 0 && (
                <TagsWrapper>
                  <Tags data={tags} />
                </TagsWrapper>
              )}
              {snippets?.length > 0 && (
                <SnippetsWrapper>
                  <Snippets data={snippets} limit={1} />
                </SnippetsWrapper>
              )}
              <ButtonWrapper>
                <BookingButton bookingType={bookingType} id={id} isAcceptingBooking={isAcceptingBooking} timeSlots={timeSlots} />
              </ButtonWrapper>
            </ColumnExpanded>
          </Anchor>
        </Link>
      </ResultWrapper>
    </Result>
  );
}

export default BusinessSearchResult;

BusinessSearchResult.propTypes = {
  activeBusinessId: PropTypes.string,
  bookingType: PropTypes.string,
  businessTags: PropTypes.shape({}),
  certifications: PropTypes.array,
  friendlyName: PropTypes.string.isRequired,
  formattedAddress: PropTypes.string,
  id: PropTypes.string.isRequired,
  isAcceptingBooking: PropTypes.bool,
  imageUrl: PropTypes.string,
  name: PropTypes.string,
  neighborHood: PropTypes.string,
  onHover: PropTypes.func,
  serviceTypes: PropTypes.array.isRequired,
  urlLocality: PropTypes.string.isRequired,
  urlRegion: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  highlights: PropTypes.array,
  snippets: PropTypes.array,
  tags: PropTypes.array,
  timeSlots: PropTypes.array,
  biz: PropTypes.shape({}),
  serviceTypeQueryParam: PropTypes.string,
};

BusinessSearchResult.defaultProps = {
  activeBusinessId: undefined,
  bookingType: undefined,
  businessTags: undefined,
  certifications: [],
  formattedAddress: undefined,
  isAcceptingBooking: undefined,
  imageUrl: undefined,
  name: "Untitled business",
  neighborHood: undefined,
  onHover: () => {},
  visible: false,
  highlights: [],
  snippets: [],
  tags: [],
  timeSlots: [],
  biz: {},
  serviceTypeQueryParam: undefined,
};
